import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, RefreshCw, Search, X, User, UserX, ChevronUp, ChevronDown,
  Pencil, Check, History, UserPlus, Trash2, Archive, ArchiveRestore, ChevronRight, SlidersHorizontal,
} from 'lucide-react';
import api from '../utils/api';
import { getContrastTextColor } from '../utils/color';
import { formatPhone } from '../utils/phoneFormatter';
import { format, parseISO } from 'date-fns';
import CustomerModal from '../components/customers/CustomerModal';
import CallHistoryModal from '../components/calls/CallHistoryModal';

const CALLS_COLS = [
  { key: 'status', label: 'סטטוס' },
  { key: 'call_label', label: 'מסע' },
  { key: 'notes', label: 'הערות' },
  { key: 'history', label: 'היסטוריה' },
];

const LAST_REFRESH_KEY = 'calls_last_refreshed';
const NEW_COUNT_POLL_MS = 3 * 60 * 1000;

export default function Calls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState(/** @type {any[]} */ ([]));
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  // Settings
  const [groupByPhone, setGroupByPhone] = useState(true);
  const [callCountDays, setCallCountDays] = useState(0);
  const [callLabels, setCallLabels] = useState(/** @type {any[]} */ ([]));

  // Filters
  const [search, setSearch] = useState('');
  const [identified, setIdentified] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('called_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pages, setPages] = useState(1);
  const [statuses, setStatuses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [hasNotesOnly, setHasNotesOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Grouping: which phone numbers are expanded
  const [expandedPhones, setExpandedPhones] = useState(/** @type {Set<string>} */ (new Set()));

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // Modals
  const [newCustomerPhone, setNewCustomerPhone] = useState(null);
  const [historyCall, setHistoryCall] = useState(null);

  // Column visibility
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [showColPicker, setShowColPicker] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(() => window.innerWidth >= 768);
  const colPickerRef = useRef(null);

  // Inline notes editing
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const noteInputRef = useRef(null);

  // Inline status editing
  const [editingStatusId, setEditingStatusId] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const sentinelRef = useRef(null);

  const pollTimerRef = useRef(null);
  const staleTimerRef = useRef(null);
  const staleSecsRef = useRef(300);
  const [isStale, setIsStale] = useState(false);

  // Load settings once
  useEffect(() => {
    api.get('/settings').then((r) => {
      const v = parseInt(r.data.stale_refresh_seconds);
      if (v > 0) staleSecsRef.current = v;
      if (r.data.calls_group_by_phone === false || r.data.calls_group_by_phone === 'false') {
        setGroupByPhone(false);
      }
      const days = parseInt(r.data.call_count_days_back);
      if (!isNaN(days)) setCallCountDays(days);
    }).catch(() => {});

    api.get('/settings/call-labels').then((r) => {
      setCallLabels(r.data || []);
    }).catch(() => {});

    api.get('/auth/me').then((r) => {
      const saved = r.data.preferences?.hidden_cols_calls;
      if (Array.isArray(saved)) setHiddenCols(new Set(saved));
    }).catch(() => {});

    return () => clearTimeout(staleTimerRef.current);
  }, []);

  // Close col picker on outside click
  useEffect(() => {
    if (!showColPicker) return;
    function handleClick(e) {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColPicker]);

  function toggleCol(key) {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      api.put('/auth/preferences', { hidden_cols_calls: [...next] }).catch(() => {});
      return next;
    });
  }

  const loadCalls = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (identified) params.identified = identified;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (!showArchived) params.archived = false;
      if (callCountDays > 0) params.call_count_days = callCountDays;

      const res = await api.get('/calls', { params });
      setCalls(prev => page === 1 ? res.data.data : [...prev, ...res.data.data]);
      setTotal(res.data.total);
      setPages(res.data.pages);

      const now = new Date().toISOString();
      localStorage.setItem(LAST_REFRESH_KEY, now);
      setHasNew(false);

      clearTimeout(staleTimerRef.current);
      setIsStale(false);
      staleTimerRef.current = setTimeout(() => setIsStale(true), staleSecsRef.current * 1000);
    } catch (err) {
      console.error('Failed to load calls:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, identified, dateFrom, dateTo, sortBy, sortDir, showArchived, refreshKey, callCountDays]);

  const checkNewCalls = useCallback(async () => {
    const since = localStorage.getItem(LAST_REFRESH_KEY);
    if (!since) return;
    try {
      const res = await api.get('/calls/new-count', { params: { since } });
      if (res.data.count > 0) setHasNew(true);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  useEffect(() => {
    pollTimerRef.current = setInterval(checkNewCalls, NEW_COUNT_POLL_MS);
    return () => clearInterval(pollTimerRef.current);
  }, [checkNewCalls]);

  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [search, identified, dateFrom, dateTo, statusFilter, hasNotesOnly, showArchived, sortBy, sortDir]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && page < pages) {
          setPage(p => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, page, pages]);

  useEffect(() => { api.get('/statuses').then(r => setStatuses(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (editingNoteId && noteInputRef.current) noteInputRef.current.focus();
  }, [editingNoteId]);

  function handleSort(col) {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  }

  function clearFilters() {
    setSearch(''); setIdentified(''); setDateFrom(''); setDateTo(''); setStatusFilter(''); setHasNotesOnly(false); setShowArchived(false);
  }

  function startEditNote(call) {
    setEditingNoteId(call.id);
    setEditingNoteText(call.customer_notes || '');
  }

  function cancelEditNote() {
    setEditingNoteId(null);
    setEditingNoteText('');
  }

  async function saveNote(callId) {
    const text = editingNoteText.trim();
    const call = calls.find(c => c.id === callId);
    if (!call?.customer_id) { setEditingNoteId(null); setEditingNoteText(''); return; }
    try {
      await api.put(`/customers/${call.customer_id}`, { notes: text || null });
      setCalls((prev) => prev.map((c) =>
        c.customer_id === call.customer_id ? { ...c, customer_notes: text || null } : c
      ));
    } catch {
      alert('שגיאה בשמירת ההערה');
    }
    setEditingNoteId(null);
    setEditingNoteText('');
  }

  async function saveStatus(customerId, newStatusId) {
    const newStatus = /** @type {any[]} */ (statuses).find(s => String(s.id) === String(newStatusId));
    try {
      await api.put(`/customers/${customerId}/status`, { status_id: newStatusId || null });
      setCalls(prev => prev.map(c =>
        c.customer_id === customerId
          ? { ...c, customer_status_id: newStatus?.id || null, customer_status: newStatus?.name || null, customer_status_color: newStatus?.color || null }
          : c
      ));
    } catch {
      alert('שגיאה בעדכון הסטטוס');
    }
    setEditingStatusId(null);
  }

  function handleNoteKeyDown(e, callId) {
    if (e.key === 'Enter') { e.preventDefault(); saveNote(callId); }
    if (e.key === 'Escape') cancelEditNote();
  }

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === displayedCalls.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedCalls.map((c) => c.id)));
    }
  }

  async function confirmAndDelete() {
    setConfirmDelete(false);
    const ids = new Set(selectedIds);
    try {
      await Promise.all([...ids].map((id) => api.delete(`/calls/${id}`)));
      setCalls(prev => prev.filter(c => !ids.has(c.id)));
      setTotal(t => t - ids.size);
      setSelectedIds(new Set());
    } catch {
      alert('שגיאה במחיקת שיחות');
    }
  }

  async function archiveCall(callId, archived) {
    try {
      await api.put(`/calls/${callId}`, { archived });
      if (!showArchived) {
        setCalls(prev => prev.filter(c => c.id !== callId));
        setTotal(t => t - 1);
      } else {
        setCalls(prev => prev.map(c => c.id === callId ? { ...c, archived } : c));
      }
    } catch {
      alert('שגיאה בארכוב השיחה');
    }
  }

  async function confirmAndArchive() {
    setConfirmArchive(false);
    const ids = new Set(selectedIds);
    try {
      await Promise.all([...ids].map((id) => api.put(`/calls/${id}`, { archived: true })));
      if (!showArchived) {
        setCalls(prev => prev.filter(c => !ids.has(c.id)));
        setTotal(t => t - ids.size);
      } else {
        setCalls(prev => prev.map(c => ids.has(c.id) ? { ...c, archived: true } : c));
      }
      setSelectedIds(new Set());
    } catch {
      alert('שגיאה בארכוב שיחות');
    }
  }

  async function markPhoneHandled(phone) {
    try {
      await api.post('/calls/handle-by-phone', { phone });
      setCalls(prev => prev.map(c =>
        c.phone === phone ? { ...c, is_handled: true, unhandled_from_phone: 0 } : c
      ));
    } catch {
      // silent
    }
  }

  function handleCustomerCreated() {
    setNewCustomerPhone(null);
    setPage(p => (p !== 1 ? 1 : p));
    setRefreshKey(k => k + 1);
  }

  function togglePhoneExpanded(phone) {
    setExpandedPhones(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  }

  const hasFilters = search || identified || dateFrom || dateTo || statusFilter || hasNotesOnly || showArchived;

  const lowerSearch = search.toLowerCase();
  const strippedSearch = search.replace(/-/g, '');
  const displayedCalls = /** @type {any[]} */ (calls)
    .filter(c => !statusFilter || String(c.customer_status_id) === String(statusFilter))
    .filter(c => !hasNotesOnly || !!c.notes)
    .filter(c => !search ||
      c.customer_name?.toLowerCase().includes(lowerSearch) ||
      c.phone?.replace(/-/g, '').includes(strippedSearch) ||
      c.notes?.toLowerCase().includes(lowerSearch)
    );

  const groupedRows = buildGroupedRows(displayedCalls, groupByPhone, expandedPhones);

  return (
    <div className="p-6">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-900 -mx-6 px-6 -mt-6 pt-6 pb-4 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Phone className="text-primary-600 dark:text-primary-400" size={22} />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">שיחות נכנסות</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">({total.toLocaleString()})</span>
        </div>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setConfirmArchive(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                         bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800
                         text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
            >
              <Archive size={15} />
              ארכיון {selectedIds.size} שיחות
            </button>
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                         bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800
                         text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            >
              <Trash2 size={15} />
              מחק {selectedIds.size} שיחות
            </button>
          </div>
        )}

        <button
          onClick={() => { setPage(1); setRefreshKey(k => k + 1); }}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                     disabled:opacity-60 transition-all duration-300 relative
                     ${isStale
                       ? 'bg-amber-500 hover:bg-amber-600 text-white border border-amber-600 shadow-md shadow-amber-300/50 dark:shadow-amber-900/50 scale-105'
                       : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                     }`}
        >
          {hasNew && <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse" />}
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          רענון
          {isStale && !hasNew && <span className="text-xs font-semibold opacity-90">לא עודכן זמן רב</span>}
          {hasNew && <span className="text-xs font-semibold">יש חדשות!</span>}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Always visible: search + toggle */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי טלפון או שם לקוח..."
              className="w-full pr-9 pl-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="rtl"
            />
          </div>
          <button
            onClick={() => setFiltersOpen(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors flex-shrink-0
              ${filtersOpen
                ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-400'
                : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'}`}
          >
            <SlidersHorizontal size={14} />
            {!filtersOpen && hasFilters && (
              <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-bold bg-primary-600 text-white">
                {[identified, statusFilter, dateFrom, dateTo, hasNotesOnly, showArchived].filter(Boolean).length}
              </span>
            )}
            <ChevronDown size={14} className={`transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Collapsible filters */}
        {filtersOpen && (
          <div className="flex flex-wrap gap-3 items-center">
            <select value={identified} onChange={(e) => setIdentified(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">כל השיחות</option>
              <option value="true">מזוהות בלבד</option>
              <option value="false">לא מזוהות בלבד</option>
            </select>

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">כל הסטטוסים</option>
              {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500" title="מתאריך" />
            <span className="text-gray-400 text-sm">עד</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500" title="עד תאריך" />

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasNotesOnly}
                onChange={(e) => setHasNotesOnly(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              עם הערה בלבד
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <Archive size={13} className="text-amber-500" />
              הצג ארכיון
            </label>

            {hasFilters && (
              <button onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700
                           dark:text-gray-400 dark:hover:text-gray-200 transition-colors">
                <X size={14} /> נקה
              </button>
            )}

            {/* Column picker */}
            <div className="relative mr-auto" ref={colPickerRef}>
              <button
                onClick={() => setShowColPicker((v) => !v)}
                title="הצג/הסתר טורים"
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors
                  ${showColPicker
                    ? 'bg-primary-50 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:border-primary-700 dark:text-primary-400'
                    : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'}`}
              >
                <SlidersHorizontal size={14} />
                טורים
                {hiddenCols.size > 0 && (
                  <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-bold bg-primary-600 text-white">
                    {hiddenCols.size}
                  </span>
                )}
              </button>
              {showColPicker && (
                <div className="absolute left-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 min-w-[140px]">
                  {CALLS_COLS.map((col) => (
                    <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-700 dark:text-gray-300 select-none">
                      <input
                        type="checkbox"
                        checked={!hiddenCols.has(col.key)}
                        onChange={() => toggleCol(col.key)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>{/* end sticky */}

      {/* Scrollable content */}
      <div className="mt-4 space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="px-3 py-3 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={displayedCalls.length > 0 && selectedIds.size === displayedCalls.length}
                    ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < displayedCalls.length; }}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                  />
                </th>
                <SortTh col="called_at" label="זמן" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                <SortTh col="phone" label="טלפון" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                <SortTh col="customer_name" label="לקוח" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                {!hiddenCols.has('status') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">סטטוס</th>}
                {!hiddenCols.has('call_label') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">מסע</th>}
                {!hiddenCols.has('notes') && <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">הערות</th>}
                {!hiddenCols.has('history') && <th className="px-3 py-3 text-center font-medium text-gray-500 dark:text-gray-400 w-16">היסטוריה</th>}
              </tr>
            </thead>
            <tbody>
              {loading && displayedCalls.length === 0 ? (
                <tr>
                  <td colSpan={20} className="px-4 py-12 text-center text-gray-400">
                    <div className="flex justify-center">
                      <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
                    </div>
                  </td>
                </tr>
              ) : displayedCalls.length === 0 ? (
                <tr>
                  <td colSpan={20} className="px-4 py-12 text-center text-gray-400">אין שיחות נכנסות</td>
                </tr>
              ) : (
                groupedRows.map((row) => (
                  <CallRow
                    key={row.call.id}
                    call={row.call}
                    isGroupHeader={row.isGroupHeader}
                    groupCount={row.groupCount}
                    isExpanded={row.isExpanded}
                    isSubRow={row.isSubRow}
                    onToggleExpand={row.isGroupHeader ? () => togglePhoneExpanded(row.call.phone) : undefined}
                    selectedIds={selectedIds}
                    toggleSelect={toggleSelect}
                    editingNoteId={editingNoteId}
                    editingNoteText={editingNoteText}
                    setEditingNoteText={setEditingNoteText}
                    noteInputRef={noteInputRef}
                    startEditNote={startEditNote}
                    saveNote={saveNote}
                    handleNoteKeyDown={handleNoteKeyDown}
                    editingStatusId={editingStatusId}
                    setEditingStatusId={setEditingStatusId}
                    statuses={statuses}
                    saveStatus={saveStatus}
                    callLabels={callLabels}
                    markPhoneHandled={markPhoneHandled}
                    archiveCall={archiveCall}
                    setHistoryCall={setHistoryCall}
                    setNewCustomerPhone={setNewCustomerPhone}
                    navigate={navigate}
                    hiddenCols={hiddenCols}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <div ref={sentinelRef} className="h-1" />
        {loading && calls.length > 0 && (
          <div className="py-4 flex justify-center border-t border-gray-200 dark:border-gray-700">
            <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        )}
        {!loading && page >= pages && calls.length > 0 && pages > 1 && (
          <div className="py-3 text-center text-xs text-gray-400 border-t border-gray-200 dark:border-gray-700">
            הוצגו כל {total.toLocaleString()} השיחות
          </div>
        )}
      </div>

      {/* New customer modal */}
      {newCustomerPhone && (
        <CustomerModal
          initialPhone={newCustomerPhone}
          onClose={() => setNewCustomerPhone(null)}
          onSaved={handleCustomerCreated}
        />
      )}

      {/* Call history modal */}
      {historyCall && (
        <CallHistoryModal
          call={historyCall}
          onClose={() => setHistoryCall(null)}
        />
      )}

      {/* Archive confirmation */}
      {confirmArchive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 w-80 space-y-4" dir="rtl">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Archive size={18} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">העברה לארכיון</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">ניתן לשחזר בכל עת</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              האם להעביר לארכיון <span className="font-bold text-amber-600 dark:text-amber-400">{selectedIds.size}</span> שיחות?
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmArchive(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                           text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                ביטול
              </button>
              <button onClick={confirmAndArchive}
                className="px-4 py-2 text-sm rounded-lg font-medium bg-amber-500 hover:bg-amber-600 text-white transition-colors">
                ארכיון {selectedIds.size} שיחות
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 w-80 space-y-4" dir="rtl">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 size={18} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">אישור מחיקה</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">פעולה זו אינה ניתנת לביטול</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              האם למחוק <span className="font-bold text-red-600 dark:text-red-400">{selectedIds.size}</span> שיחות?
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                           text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                ביטול
              </button>
              <button onClick={confirmAndDelete}
                className="px-4 py-2 text-sm rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors">
                מחק {selectedIds.size} שיחות
              </button>
            </div>
          </div>
        </div>
      )}
      </div>{/* end scrollable */}
    </div>
  );
}

/**
 * Group calls by phone number for display.
 * Each unique phone gets one header row (most recent call);
 * additional calls from the same phone become sub-rows shown when expanded.
 */
function buildGroupedRows(calls, groupByPhone, expandedPhones) {
  if (!groupByPhone) {
    return calls.map(call => ({
      call,
      isGroupHeader: false,
      groupCount: 1,
      isExpanded: false,
      isSubRow: false,
    }));
  }

  const rows = [];
  const seenIdx = new Map(); // phone → index in rows[]

  for (const call of calls) {
    const phone = call.phone;
    if (seenIdx.has(phone)) {
      const headerIdx = seenIdx.get(phone);
      rows[headerIdx].groupCount++;
      rows[headerIdx].isGroupHeader = true;
      if (expandedPhones.has(phone)) {
        rows.push({ call, isGroupHeader: false, groupCount: 1, isExpanded: false, isSubRow: true });
      }
    } else {
      seenIdx.set(phone, rows.length);
      rows.push({
        call,
        isGroupHeader: false,
        groupCount: 1,
        isExpanded: expandedPhones.has(phone),
        isSubRow: false,
      });
    }
  }

  return rows;
}

function CallRow({
  call,
  isGroupHeader,
  groupCount,
  isExpanded,
  isSubRow,
  onToggleExpand,
  selectedIds,
  toggleSelect,
  editingNoteId,
  editingNoteText,
  setEditingNoteText,
  noteInputRef,
  startEditNote,
  saveNote,
  handleNoteKeyDown,
  editingStatusId,
  setEditingStatusId,
  statuses,
  saveStatus,
  callLabels,
  markPhoneHandled,
  archiveCall,
  setHistoryCall,
  setNewCustomerPhone,
  navigate,
  hiddenCols,
}) {
  const unhandled = call.unhandled_from_phone > 0;
  const callMatchedLabels = (call.call_tags || [])
    .map(v => callLabels.find(l => l.value === v))
    .filter(Boolean);

  return (
    <tr
      className={`border-b hover:brightness-95 dark:hover:brightness-110
        ${isSubRow
          ? 'border-blue-100 dark:border-blue-900/40 bg-blue-50/70 dark:bg-blue-950/40'
          : 'border-gray-100 dark:border-gray-700/50'}
        ${selectedIds.has(call.id) ? 'bg-red-50/50 dark:bg-red-900/10' : ''}
        ${call.archived ? 'opacity-60' : ''}`}
    >
      {/* Checkbox */}
      <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedIds.has(call.id)}
          onChange={() => toggleSelect(call.id)}
          className="rounded border-gray-300 text-red-500 focus:ring-red-400"
        />
      </td>

      {/* Time */}
      <td className={`px-4 py-3 text-center whitespace-nowrap text-xs
        ${isSubRow ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
        <div>{format(parseISO(call.called_at), 'dd/MM/yyyy')}</div>
        <div>{format(parseISO(call.called_at), 'HH:mm')}</div>
      </td>

      {/* Phone */}
      <td className="px-4 py-3 text-center whitespace-nowrap" dir="ltr">
        <div className="flex items-center justify-center gap-1.5">
          {/* Expand/collapse button for group headers */}
          {isGroupHeader && (
            <button
              onClick={onToggleExpand}
              className="text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors shrink-0"
              title={isExpanded ? 'כווץ' : `הרחב (${groupCount} שיחות)`}
            >
              <ChevronRight size={13} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </button>
          )}

          {/* Phone link */}
          <a
            href={`tel:${call.phone}`}
            className="font-mono font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            {formatPhone(call.phone)}
          </a>

          {/* Call count / unhandled badge */}
          {unhandled ? (
            <button
              onClick={(e) => { e.preventDefault(); markPhoneHandled(call.phone); }}
              title="סמן כטופל"
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold
                         bg-red-500 text-white hover:bg-red-600 transition-colors leading-none"
            >
              {call.phone_call_count >= 2 ? call.phone_call_count : ''}
            </button>
          ) : (
            call.phone_call_count > 1 && (
              <span
                className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 rounded-full px-1.5 py-0.5 leading-none"
                title={`${call.phone_call_count} שיחות מהמספר הזה`}
              >
                {call.phone_call_count}
              </span>
            )
          )}

          {/* Group count */}
          {isGroupHeader && groupCount > 1 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">({groupCount})</span>
          )}
        </div>
        {call.phone_label && (
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5" dir="rtl">{call.phone_label}</div>
        )}
      </td>

      {/* Customer */}
      <td className="px-4 py-3 text-center">
        {call.customer_id ? (
          <div>
            <button
              onClick={() => navigate(`/customers/${call.customer_id}`)}
              className="inline-flex items-center gap-1.5 text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              <User size={14} />
              {call.customer_name}
            </button>
            {call.customer_city && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{call.customer_city}</div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setNewCustomerPhone(call.phone)}
            title="צור לקוח חדש עם מספר זה"
            className="inline-flex items-center gap-1.5 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:underline transition-colors"
          >
            <UserX size={14} />
            לא מזוהה
            <UserPlus size={12} className="opacity-60" />
          </button>
        )}
      </td>

      {/* Status */}
      {!hiddenCols.has('status') && (
        <td className="px-4 py-3 text-center">
          {call.customer_id ? (
            editingStatusId === call.id ? (
              <select
                autoFocus
                defaultValue={call.customer_status_id || ''}
                onChange={(e) => saveStatus(call.customer_id, e.target.value)}
                onBlur={() => setEditingStatusId(null)}
                className="text-xs rounded border border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-1 py-0.5 max-w-[120px]"
              >
                <option value="">ללא סטטוס</option>
                {/** @type {any[]} */ (statuses).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            ) : (
              <button
                onClick={() => setEditingStatusId(call.id)}
                title="לחץ לשינוי סטטוס"
                className="group inline-flex items-center gap-1"
              >
                {call.customer_status ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: call.customer_status_color || '#6B7280', color: getContrastTextColor(call.customer_status_color || '#6B7280') }}
                  >
                    {call.customer_status}
                    <Pencil size={9} className="opacity-0 group-hover:opacity-70 transition-opacity" />
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    — <Pencil size={9} className="inline opacity-0 group-hover:opacity-50 transition-opacity" />
                  </span>
                )}
              </button>
            )
          ) : (
            call.customer_status && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: call.customer_status_color || '#6B7280', color: getContrastTextColor(call.customer_status_color || '#6B7280') }}
              >
                {call.customer_status}
              </span>
            )
          )}
        </td>
      )}

      {/* מסע (call label) */}
      {!hiddenCols.has('call_label') && (
        <td className="px-4 py-3 text-center">
          {callMatchedLabels.length > 0 ? (
            <div className="flex flex-wrap gap-1 justify-center">
              {callMatchedLabels.map(lbl => (
                <span key={lbl.value} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                               bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                  {lbl.label}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-300 dark:text-gray-600">—</span>
          )}
        </td>
      )}

      {/* Notes — customer note (editable, saves to customer) */}
      {!hiddenCols.has('notes') && (
        <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 text-xs">
          {editingNoteId === call.id ? (
            <div className="flex items-center gap-1 justify-center">
              <input
                ref={noteInputRef}
                value={editingNoteText}
                onChange={(e) => setEditingNoteText(e.target.value)}
                onKeyDown={(e) => handleNoteKeyDown(e, call.id)}
                onBlur={() => saveNote(call.id)}
                className="w-40 px-2 py-1 text-xs rounded border border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="הערה על הלקוח..."
              />
              <button onMouseDown={(e) => { e.preventDefault(); saveNote(call.id); }}
                className="p-1 text-emerald-500 hover:text-emerald-700">
                <Check size={13} />
              </button>
            </div>
          ) : call.customer_id ? (
            <button
              onClick={() => startEditNote(call)}
              title="לחץ לעריכה"
              className="group inline-flex items-center gap-1 max-w-[160px] text-right hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <span className="truncate">{call.customer_notes || '—'}</span>
              <Pencil size={11} className="shrink-0 opacity-0 group-hover:opacity-50 transition-opacity" />
            </button>
          ) : (
            <span className="text-gray-300 dark:text-gray-600">—</span>
          )}
        </td>
      )}

      {/* History + Archive */}
      {!hiddenCols.has('history') && (
        <td className="px-3 py-3 text-center">
          <div className="flex items-center justify-center gap-1">
            <button
              onClick={() => setHistoryCall(call)}
              title="היסטוריית התקשרויות"
              className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              <History size={14} />
            </button>
            <button
              onClick={() => archiveCall(call.id, !call.archived)}
              title={call.archived ? 'הוצא מארכיון' : 'העבר לארכיון'}
              className={`p-1.5 rounded transition-colors
                ${call.archived
                  ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                  : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                }`}
            >
              {call.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

function SortTh({ col, label, sortBy, sortDir, onClick }) {
  const active = sortBy === col;
  return (
    <th
      className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
      onClick={() => onClick(col)}
    >
      <span className="flex items-center gap-1 justify-center">
        {label}
        {active
          ? (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)
          : <ChevronDown size={14} className="opacity-30" />
        }
      </span>
    </th>
  );
}
